import { IcBattery, IcDroneFlight, IcRotor, IcSettings } from '@assets/icons/24x24';
import { Button, Center, Field as ChakraField, Fieldset, Grid, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import ErrorsContainer from '@components/common/form/ErrorsContainer';
import { useMapContext } from '@components/map/MapContext';
import TargetId from '@components/map/popup/infos/targets/TargetId';
import { GPSCoordinatesField } from '@components/ui/coordinatesInput';
import { Form } from '@components/ui/Form';
import { InputNumber } from '@components/ui/input/InputNumber';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveOrganization, selectActiveUser } from '@redux/authent/authent.selectors';
import { makeSelectPlatformsNamesByCode } from '@redux/config/config.selectors';
import { usePostGroundStationCommandMutation } from '@services/c2/c2.api';
import { getPlatformsCodesFromMarks } from '@utils/common.utils';
import { getTargetDisplayedId, getTargetIcon } from '@utils/target.utils';
import { Field, Formik } from 'formik';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { shallowEqual } from 'react-redux';

import { GroundStationCommand, GroundStationCommandEnum, Target } from '@/types/c2/c2.types';
import { Coordinates } from '@/types/commons/commons.types';
import { LandedStateEnum, TargetLocation } from '@/types/sensor/sensor.types';
import { TrackLight } from '@/types/sensor/sensors';

type Props = {
  target: Target<TargetLocation>;
  groundStationTrack: TrackLight;
};

export default function DroneControlCommands({ target, groundStationTrack }: Readonly<Props>) {
  const { mouseSelectedPosition, isSelectionEnabled, setSelectionOn } = useMapContext();
  const platformCodes = getPlatformsCodesFromMarks(target.marks);
  const selectPlatformNames = useMemo(makeSelectPlatformsNamesByCode, []);
  const platforms = useAppSelector((state) => selectPlatformNames(state, platformCodes), shallowEqual);
  const organization = useAppSelector(selectActiveOrganization);
  const { activeSite } = useAppSelector(selectActiveUser);
  const site = activeSite?.code ?? '';
  const appCode = groundStationTrack.appCode;
  const [postCommand, { isLoading }] = usePostGroundStationCommandMutation();
  const [height, setHeight] = useState<number | undefined>(60);

  const model = target.technicalData?.droneModel;
  const modelLabel = model !== undefined ? ` ${model}` : '';

  const batteryRemaining = target.technicalData?.batteryRemaining ?? 0;
  const timeRemaining = target.technicalData?.timeRemaining ?? 0;
  const landedState = target.technicalData?.landedState ?? LandedStateEnum.MAV_LANDED_STATE_UNDEFINED;
  const armed = target.technicalData?.armed ?? false;

  const initialGoToPoint: {
    height: number;
    position: Coordinates;
  } = {
    height: 0,
    position: {
      latitude: 0,
      longitude: 0,
      type: '2D',
    },
  };

  const handlePostCommand = (command: Partial<GroundStationCommand>) => {
    postCommand({
      site,
      appCode,
      command: {
        ...command,
        systemId: target.technicalData!.systemId,
        sensorCode: groundStationTrack.sensorCode,
        componentId: target.technicalData!.autopilotId,
      } as GroundStationCommand,
    });
  };

  const handleSubmit = (goToPoint: { height: number; position: Coordinates }) => {
    if (landedState !== LandedStateEnum.MAV_LANDED_STATE_ON_GROUND) {
      handlePostCommand({
        type: GroundStationCommandEnum.GO_TO,
        latitude: goToPoint.position.latitude,
        longitude: goToPoint.position.longitude,
        speed: null,
        height: goToPoint.height,
      });
    }
  };

  return (
    <VStack width="100%" gap={0.25} backgroundColor="neutral.900">
      <HStack width="100%" height="120px" gap={2} backgroundColor="neutral.700" justifyContent="center">
        <Icon as={getTargetIcon(target, false, organization)} width="70px" height="70px" />
        <VStack gap={0.5} alignItems="start">
          <TargetId label={`${getTargetDisplayedId(target)}${modelLabel}`} isTechnical={target.displayId === null} />
          <HStack maxWidth="100%" gap={0.5}>
            {platforms.map((platform) => (
              <Tooltip key={platform} content={platform} openDelay={200}>
                <Text
                  paddingX={0.25}
                  backgroundColor="neutral.600"
                  textTransform="uppercase"
                  textStyle="md"
                  fontWeight="medium"
                  color="neutral.200"
                >
                  {platform.slice(0, 4)}
                </Text>
              </Tooltip>
            ))}
          </HStack>
        </VStack>
      </HStack>
      <Grid
        width="100%"
        height="126px"
        backgroundColor="neutral.700"
        templateRows="repeat(2, 1fr)"
        templateColumns="repeat(2, 1fr)"
        padding={3}
        gap={1.5}
      >
        <HStack gap={1}>
          <Icon as={IcBattery} boxSize="24px" color="neutral.300" />
          <Text fontWeight="medium">{`${batteryRemaining}%`}</Text>
          <Text fontWeight="medium">{`${timeRemaining / 60} min`}</Text>
        </HStack>
        <HStack gap={1}>
          <Icon as={IcSettings} boxSize="24px" color="neutral.300" />
          <Text fontWeight="medium">{target.technicalData?.flyingMode ?? '-'}</Text>
        </HStack>
        <HStack gap={1}>
          <Icon as={IcRotor} boxSize="24px" color="neutral.300" />
          <Text fontWeight="medium">
            <FormattedMessage id="target.technicalData.armed" values={{ armed }} />
          </Text>
        </HStack>
        <HStack gap={1}>
          <Icon as={IcDroneFlight} boxSize="24px" color="neutral.300" />
          <Text fontWeight="medium">
            <FormattedMessage id={`target.technicalData.landedState.${landedState}`} />
          </Text>
        </HStack>
      </Grid>
      <Center width="100%" padding={4} backgroundColor="neutral.700">
        <Button
          variant="primary"
          disabled={landedState !== LandedStateEnum.MAV_LANDED_STATE_ON_GROUND}
          loading={isLoading}
          onClick={() =>
            landedState === LandedStateEnum.MAV_LANDED_STATE_ON_GROUND &&
            handlePostCommand({ type: GroundStationCommandEnum.ARM, arm: !armed })
          }
        >
          <FormattedMessage id="droneControl.tab.commands.armRotors" values={{ armed }} />
        </Button>
      </Center>
      <VStack width="100%" gap={1.5} padding={4} backgroundColor="neutral.700">
        {landedState === LandedStateEnum.MAV_LANDED_STATE_ON_GROUND && (
          <Text>
            <FormattedMessage id="droneControl.tab.commands.takeOffHeight" />
          </Text>
        )}
        <HStack gap={2} width="100%" justifyContent="center">
          {landedState === LandedStateEnum.MAV_LANDED_STATE_ON_GROUND && (
            <InputNumber value={height} onChange={(value) => setHeight(value)} invalid={height === undefined} />
          )}
          <Button
            variant="primary"
            disabled={landedState === LandedStateEnum.MAV_LANDED_STATE_ON_GROUND && height === undefined}
            loading={isLoading}
            onClick={() => {
              if (landedState !== LandedStateEnum.MAV_LANDED_STATE_ON_GROUND || height !== undefined) {
                const command =
                  landedState === LandedStateEnum.MAV_LANDED_STATE_ON_GROUND
                    ? { type: GroundStationCommandEnum.TAKEOFF, height }
                    : { type: GroundStationCommandEnum.LAND };
                handlePostCommand(command);
              }
            }}
          >
            <FormattedMessage
              id={`droneControl.tab.commands.${landedState === LandedStateEnum.MAV_LANDED_STATE_ON_GROUND ? 'takeOff' : 'land'}`}
            />
          </Button>
        </HStack>
      </VStack>
      <VStack width="100%" padding={4} gap={3} backgroundColor="neutral.700">
        <Formik initialValues={initialGoToPoint} onSubmit={handleSubmit}>
          {({ handleSubmit, values, errors, touched, setFieldValue }) => (
            <Form style={{ width: '100%' }} onSubmit={handleSubmit}>
              <Fieldset.Root
                invalid={
                  (!!errors.height && touched.height) ||
                  (!!errors.position && (touched.position?.latitude || touched.position?.longitude))
                }
              >
                <Fieldset.Content>
                  <Fieldset.Root invalid={touched.position && !!errors.position}>
                    <HStack>
                      <Text fontSize="16px">
                        <FormattedMessage id="droneControl.tab.commands.goTo" />
                      </Text>
                      <Text color="red" fontWeight="bold">
                        *
                      </Text>
                    </HStack>
                    <Fieldset.Content>
                      <GPSCoordinatesField
                        name="position"
                        mouseSelectedPosition={mouseSelectedPosition}
                        isSelectionEnabled={isSelectionEnabled}
                        setSelectionOn={setSelectionOn}
                      />
                    </Fieldset.Content>
                  </Fieldset.Root>
                  <ChakraField.Root required invalid={touched.height && !!errors.height}>
                    <ChakraField.Label>
                      <Text fontSize="16px">
                        <FormattedMessage id="droneControl.tab.commands.height" />
                      </Text>
                    </ChakraField.Label>
                    <Field
                      as={InputNumber}
                      value={values.height}
                      onChange={(value: number | undefined) => setFieldValue('height', value)}
                      suffix="m"
                    />
                  </ChakraField.Root>
                </Fieldset.Content>
                {Object.keys(errors).some((key) => touched[key as keyof typeof touched]) && (
                  <Fieldset.ErrorText>
                    <ErrorsContainer errors={errors} touched={touched} />
                  </Fieldset.ErrorText>
                )}
                <Center>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={landedState === LandedStateEnum.MAV_LANDED_STATE_ON_GROUND || height === undefined}
                    loading={isLoading}
                  >
                    <FormattedMessage id="droneControl.tab.commands.goToPoint" />
                  </Button>
                </Center>
              </Fieldset.Root>
            </Form>
          )}
        </Formik>
      </VStack>
    </VStack>
  );
}
